/** @graphql agg **/
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useIsAutoConvertedRecentlyQuery$data,
	useIsAutoConvertedRecentlyQuery$variables,
} from './__generated__/useIsAutoConvertedRecentlyQuery.graphql.ts';

export const UseIsAutoConvertedRecentlyQuery = graphql`
	query useIsAutoConvertedRecentlyQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				properties(key: "editor") {
					nodes {
						value
					}
				}
				metadata {
					frontend {
						fabricEditorSupported
					}
				}
				contentProperties {
					draft {
						autoConvertedDate
					}
				}
			}
		}
	}
`;

type UseIsAutoConvertedRecentlyReturnType = {
	loading: boolean;
	error?: Error;
	isAutoConverted: boolean;
	isMarkedAutoConverted: boolean;
};

export const useIsAutoConvertedRecently = ({
	contentId,
}: {
	contentId: string;
}): UseIsAutoConvertedRecentlyReturnType => {
	const { data, loading, error } = useQueryInterop<
		useIsAutoConvertedRecentlyQuery$data,
		useIsAutoConvertedRecentlyQuery$variables
	>(UseIsAutoConvertedRecentlyQuery, {
		variables: { contentId },
	});

	const node = data?.content?.nodes?.[0];
	// isFabricEditor: Is this page persisted as a v2 Fabric page, or a legacy page (v1/null)?
	const isFabricEditor = node?.properties?.nodes?.[0]?.value === '"v2"';
	// isFabricEditorSupported: Is this page eligible for conversion from legacy to Fabric?
	const isFabricEditorSupported = !!node?.metadata?.frontend?.fabricEditorSupported;
	// isAutoConverted: A page is auto-converted if it is not persisted as Fabric, and is conversion eligible.
	const isAutoConverted = !isFabricEditor && isFabricEditorSupported;
	// isMarkedAutoConverted: Does this page already have the autoConvertedDate content property? (i.e. has this auto-converted page been previously visited?)
	const isMarkedAutoConverted = !!node?.contentProperties?.draft?.autoConvertedDate;

	return {
		loading,
		error,
		isAutoConverted,
		isMarkedAutoConverted,
	};
};
